<template lang="html">
    <report-section
        :id="id"
        title="Annual Water Supply and Demand"
    >
        <p>
            This section describes the annual water supply and demand, for the location ({{ queryWatershedName }}) that
            you selected.
            <span class="is-hidden-mobile">
                The watershed is outlined in orange on the map below. The watershed associated with the next
                downstream confluence<sup>
                    <footnote-tooltip
                        :footnote-number="2"
                    />
                </sup> ({{ downstreamWatershedName }}) has also been outlined in purple, with summary statistics for
                both watersheds provided in the table below. Please note that all values presented are estimates and
                are subject to error<sup>
                    <footnote-tooltip
                        :footnote-number="3"
                    />
                </sup>.
            </span>
        </p>
        <mgmt-basin-map
            class="mgmt-basin-map is-hidden-mobile"
            :query-coords="reportData.lngLat"
            :mgmt-basin="managementBasin"
            :query-basin="queryBasin"
            query-location-text="Query Watershed"
            mgmt-outlet-text="Downstream Watershed"
            :map-style="mapStyle"
            v-bind="mapColors"
            @load="$emit('load')"
            @error="$emit('error')"
        />

        <div v-if="pdf">
            <hr>

            <h4 class="title is-5 header">
                Legend
            </h4>

            <chart-legend
                :items="legendItems"
                class="legend"
            />

            <hr>
        </div>

        <annual-hydrology-table
            class="annual-hydrology-table"
            :data="tableData"
            :pdf="pdf"
        />
    </report-section>
</template>

<script>
import ChartLegend from '@foundryspatial/chart-legend';
import MgmtBasinMap from '@foundryspatial/mgmt-basin-map';
import sectionMixin from '@foundryspatial/report-section/mixin';

import scssVars from '@/assets/scss/_variables.scss';
import AnnualHydrologyTable from '@/components/AnnualHydrologyTable.vue';

export default {
    name: 'AnnualHydrologySection',

    components: {
        AnnualHydrologyTable,
        MgmtBasinMap,
        ChartLegend,
    },

    mixins: [
        sectionMixin,
    ],

    props: {
        // true on the PDF report
        pdf: {
            type: Boolean,
            required: false,
            default: false,
        },
    },

    computed: {
        tableData() {
            return {
                ...this.reportData.annualHydrology,
                queryWatershedName: this.queryWatershedName,
                downstreamWatershedName: this.downstreamWatershedName,
            };
        },

        queryWatershedName() {
            return this.reportData.overview.watershedName;
        },

        downstreamWatershedName() {
            return this.reportData.overview.mgmt_name;
        },

        managementBasin() {
            const { overview } = this.reportData;
            return {
                polygon: overview.mgmt_polygon,
                outletCoordinates: {
                    lng: overview.mgmt_lng,
                    lat: overview.mgmt_lat,
                },
            };
        },

        queryBasin() {
            return {
                polygon: this.reportData.overview.query_polygon,
            };
        },

        // color props for the minimap component
        mapColors() {
            return {
                mgmtOutletMarkerColor: scssVars.colorWatershedMgmt,
                mgmtWatershedColor: scssVars.colorWatershedMgmt,
                mgmtWatershedOutlineColor: scssVars.colorWatershedMgmtBorder,
                queryMarkerColor: scssVars.colorWatershedQuery,
                queryWatershedColor: scssVars.colorWatershedQuery,
                queryWatershedOutlineColor: scssVars.colorWatershedQueryBorder,
            };
        },

        // mapbox style for the mini-map
        mapStyle() {
            return process.env.VUE_APP_MAPBOX_DEFAULT_STYLE;
        },

        legendItems() {
            return [
                {
                    color: this.mapColors.queryWatershedColor,
                    borderColor: '#000',
                    legendLabel: 'Query Watershed',
                    legendType: 'box',
                },
                {
                    color: this.mapColors.mgmtWatershedColor,
                    borderColor: '#000',
                    legendLabel: 'Downstream Watershed',
                    legendType: 'box',
                },
            ];
        },
    },
};
</script>

<style lang="scss">
#annual-hydrology.report-section {
    .mgmt-basin-map {
        height: 30em;
        margin-bottom: 1em;
    }
}

.static-report #annual-hydrology.report-section {
    .header {
        font-size: 1.25rem;
        line-height: 1.25rem;
    }

    .mgmt-basin-map {
        height: 320px;

        .mgmt-basin-map-legend {
            display: none;
        }
    }

    .legend {
        width: 100%;
        justify-content: space-between;

        .legend-item {
            margin-left: 0;
            margin-right: 0;

            .row > * {
                margin: 0 0.3em;
            }

            .visual-container {
                height: 1em;
                width: 1em;

                .color-box {
                    height: 1em;
                }
            }
        }
    }

    .table {
        th {
            font-size: 0.7rem;
        }

        td {
            font-size: 0.625rem;
        }
    }
}
</style>
